import './styles.css';
import { h } from './core/dom';
import { startRouter, route } from './core/router';
import { dashboardRoute } from './routes/dashboard';
import { transactionsRoute } from './routes/transactions';
import { budgetsRoute } from './routes/budgets';
import { categoriesRoute } from './routes/categories';
import { debtsListRoute } from './routes/debtsList';
import { debtDetailRoute } from './routes/debtDetail';
import { MOCK } from './data';
import { isAuthenticated, login, logout, userEmail, handleRedirectCallback } from './auth';
import { registerHeaderSlot } from './components/headerSlot';

const app = document.getElementById('app')!;

function renderShell(): void {
	// <main> landmark: the route outlet is the page's main content region.
	// tabindex=-1 so the skip link can move focus into it programmatically.
	const outlet = h('main', { tabindex: '-1' });
	// A button, not <a href="#...">: fragment hrefs would drive the hash router.
	const skip = h('button', { class: 'skip-link', onclick: () => outlet.focus() }, 'Skip to content');
	// Right-aligned slot in the top nav that month-scoped routes fill with the
	// MonthNav switcher (and other routes clear). Registered once here.
	const headerSlot = h('div', { class: 'topnav-control' });
	registerHeaderSlot(headerSlot);

	// Nav links + an active-route indicator: the link matching the current
	// top-level path gets .active (heavier weight) so the current view is obvious.
	const navItems: { path: string; label: string }[] = [
		{ path: '/', label: 'Dashboard' },
		{ path: '/transactions', label: 'Transactions' },
		{ path: '/budgets', label: 'Budgets' },
		{ path: '/debts', label: 'Debts' },
		{ path: '/categories', label: 'Categories' },
	];
	const navLinks = navItems.map((it) => h('a', { href: `#${it.path}` }, it.label));
	// On narrow screens the links collapse behind a hamburger (CSS hides the
	// toggle on desktop). Navigation closes the menu via the hashchange listener.
	const navList = h('div', { class: 'topnav-links', id: 'topnav-links' }, ...navLinks);
	const navToggle = h(
		'button',
		{
			class: 'nav-toggle',
			'aria-label': 'Menu',
			'aria-expanded': 'false',
			'aria-controls': 'topnav-links',
			onclick: () => setMenuOpen(!topnav.classList.contains('open')),
		},
		'☰'
	) as HTMLButtonElement;
	const setMenuOpen = (open: boolean) => {
		topnav.classList.toggle('open', open);
		navToggle.setAttribute('aria-expanded', String(open));
		navToggle.textContent = open ? '✕' : '☰';
	};
	const updateActiveNav = () => {
		const path = (location.hash.slice(1) || '/').split('?')[0];
		navItems.forEach((it, i) => {
			const active = it.path === '/' ? path === '/' : path === it.path || path.startsWith(it.path + '/');
			navLinks[i].classList.toggle('active', active);
			if (active) navLinks[i].setAttribute('aria-current', 'page');
			else navLinks[i].removeAttribute('aria-current');
		});
	};
	addEventListener('hashchange', () => {
		updateActiveNav();
		setMenuOpen(false);
	});
	updateActiveNav();

	// Signed-in email + sign-out, like the Svelte header's user menu. Hidden in
	// mock mode (no session to show).
	const userMenu = MOCK
		? null
		: h(
				'div',
				{ class: 'user-menu' },
				userEmail() ? h('span', { class: 'user-email' }, userEmail()!) : null,
				h('button', { class: 'btn btn-sm btn-secondary', onclick: () => logout() }, 'Sign out')
			);

	const topnav = h('nav', { class: 'topnav' }, navToggle, navList, headerSlot, userMenu);
	app.replaceChildren(h('div', { class: 'app-shell' }, skip, topnav, outlet));
	// Per-route document titles so history/tabs/screen readers name the view.
	const titled = <A extends unknown[], R>(title: string, render: (...args: A) => R) =>
		(...args: A): R => {
			document.title = `${title} — Family Finance`;
			return render(...args);
		};
	startRouter(outlet, [
		route('/', titled('Dashboard', (_p, q) => dashboardRoute(q))),
		route('/transactions', titled('Transactions', (_p, q) => transactionsRoute(q))),
		route('/budgets', titled('Budgets', (_p, q) => budgetsRoute(q))),
		route('/categories', titled('Categories', () => categoriesRoute())),
		route('/debts', titled('Debts', (_p, q) => debtsListRoute(q))),
		route('/debts/:id', titled('Debt', (p) => debtDetailRoute(p))),
	]);
}

function renderLogin(): void {
	// Our own button — clicking it redirects to Google (no GSI library, no
	// injected styles). CSP stays at style-src 'self'.
	app.replaceChildren(
		h(
			'div',
			{ class: 'app-shell' },
			h(
				'div',
				{ class: 'card' },
				h('h1', {}, 'Family Finance'),
				h('p', { class: 'muted' }, 'Sign in with Google to continue.'),
				h('button', { class: 'btn btn-primary', onclick: () => login() }, 'Sign in with Google')
			)
		)
	);
}

// Capture a token if we've just returned from Google, then gate the app.
handleRedirectCallback();
if (MOCK || isAuthenticated()) renderShell();
else renderLogin();
